import { all, call, put, takeLatest } from '@redux-saga/core/effects';
import axios, { AxiosResponse } from 'axios';

import { SagaPayload } from '../api.types';
import {
  AddMemberToTeamRequest,
  AddMemberToTeamResponse,
  RemoveMemberFromTeamRequest,
  RemoveMemberFromTeamResponse
} from './team-member.types';

import { ADDING_MEMBER_TO_TEAM, REMOVING_MEMBER_FROM_TEAM } from './team-member.constants';

import { requestAddMemberToTeam, requestRemoveMemberFromTeam } from './team-member.actions';

import { getAddMemberToTeamRequest, getRemoveMemberFromTeamRequest } from './team-member.endpoints';

import { addMemberToTeam, removeMemberFromTeam } from '../../slices/programme/team/team.slice';
import { addSpinner, addSystemNotice, removeSpinner } from '../../slices/system/system.slice';

import { getErrorMessage } from '../../utils/api.util';
import { ERROR, SUCCESS } from '../../utils/constants';

export function* performAddMemberToTeamRequest({ payload }: SagaPayload<AddMemberToTeamRequest>) {
  const { requestData, onSuccess } = payload;

  try {
    yield put(addSpinner(ADDING_MEMBER_TO_TEAM));

    const request = getAddMemberToTeamRequest(requestData);

    const { data }: AxiosResponse<AddMemberToTeamResponse> = yield call(axios, ...request);

    yield put(
      addMemberToTeam({
        member: data.teamMember,
        teamExternalReference: requestData.teamExternalReference
      })
    );

    if (onSuccess) {
      yield call(onSuccess, undefined);
    }

    yield put(addSystemNotice({ type: SUCCESS, message: data.message }));
  } catch (error) {
    const message = getErrorMessage(error);
    yield put(addSystemNotice({ type: ERROR, message }));
  } finally {
    yield put(removeSpinner(ADDING_MEMBER_TO_TEAM));
  }
}

export function* watchForAddMemberToTeamRequest() {
  yield takeLatest(requestAddMemberToTeam.type, performAddMemberToTeamRequest);
}

export function* performRemoveMemberFromTeamRequest({ payload }: SagaPayload<RemoveMemberFromTeamRequest>) {
  const { requestData, onSuccess } = payload;

  try {
    yield put(addSpinner(REMOVING_MEMBER_FROM_TEAM));

    const request = getRemoveMemberFromTeamRequest(requestData);

    const { data }: AxiosResponse<RemoveMemberFromTeamResponse> = yield call(axios, ...request);

    yield put(removeMemberFromTeam(requestData));

    if (onSuccess) {
      yield call(onSuccess, undefined);
    }

    yield put(addSystemNotice({ type: SUCCESS, message: data.message }));
  } catch (error) {
    const message = getErrorMessage(error);
    yield put(addSystemNotice({ type: ERROR, message }));
  } finally {
    yield put(removeSpinner(REMOVING_MEMBER_FROM_TEAM));
  }
}

export function* watchForRemoveMemberFromTeamRequest() {
  yield takeLatest(requestRemoveMemberFromTeam.type, performRemoveMemberFromTeamRequest);
}

export default function* teamMemberSaga() {
  yield all([watchForAddMemberToTeamRequest(), watchForRemoveMemberFromTeamRequest()]);
}
